import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { executePromise } from "../../util/sliceUtil";
import { useCountry } from "../../hooks/hooks";
import { CountryReportPolicyApi } from "../../api/CountryReportPolicyApi";
import { CountryReportPolicy as Policy, ReportTypeValue } from "../../types/countryReportPolicy";
import style from "./countryReportPolicy.module.scss";

interface FormState {
  idx: number | null; // null = 신규
  countryCode: string;
  defaultReportType: ReportTypeValue;
  allowBoth: boolean;
}

const EMPTY_FORM: FormState = {
  idx: null,
  countryCode: "",
  defaultReportType: ReportTypeValue.STANDARD,
  allowBoth: true,
};

export function CountryReportPolicy() {
  const { t } = useTranslation("countryReportPolicy");

  const [policies, setPolicies] = useState<Policy[]>([]);
  const [form, setForm] = useState<FormState | null>(null);
  const [loading, setLoading] = useState(false);

  // 검사결과지에서 실제 쓰는 국적 국가만 노출 (전체 ISO 국가 X)
  const { onlyNationalityOptions, getCountryNameByCodeISOAlpha2 } = useCountry();

  const countryName = useCallback(
    (code: string) => getCountryNameByCodeISOAlpha2(code) ?? code,
    [getCountryNameByCodeISOAlpha2]
  );

  const loadPolicies = useCallback(async () => {
    try {
      setLoading(true);
      // HttpClient 응답 인터셉터가 response.data 를 벗겨서 반환하므로 res 자체가 payload
      const res = await executePromise(CountryReportPolicyApi.getPolicies());
      setPolicies((res ?? []) as unknown as Policy[]);
    } catch (e) {
      console.error(e);
      alert(t("message.loadError"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    loadPolicies();
  }, [loadPolicies]);

  const reportTypeLabel = (v: string) =>
    v === ReportTypeValue.BRAIN_ONLY ? t("reportType.brainOnly") : t("reportType.standard");

  const openCreate = () => setForm({ ...EMPTY_FORM });
  const openEdit = (p: Policy) =>
    setForm({
      idx: p.idx,
      countryCode: p.countryCode,
      defaultReportType: p.defaultReportType,
      allowBoth: p.allowBoth,
    });
  const closeForm = () => setForm(null);

  const save = async () => {
    if (!form) return;
    if (!form.countryCode) {
      alert(t("message.countryRequired"));
      return;
    }
    const param = {
      countryCode: form.countryCode,
      defaultReportType: form.defaultReportType,
      allowBoth: form.allowBoth,
    };
    try {
      setLoading(true);
      if (form.idx == null) {
        await executePromise(CountryReportPolicyApi.createPolicy(param));
      } else {
        await executePromise(CountryReportPolicyApi.updatePolicy(form.idx, param));
      }
      alert(t("message.saved"));
      closeForm();
      await loadPolicies();
    } catch (e) {
      console.error(e);
      alert(t("message.saveError"));
    } finally {
      setLoading(false);
    }
  };

  const remove = async (p: Policy) => {
    if (!window.confirm(t("message.deleteConfirm"))) return;
    try {
      setLoading(true);
      await executePromise(CountryReportPolicyApi.deletePolicy(p.idx));
      alert(t("message.deleted"));
      await loadPolicies();
    } catch (e) {
      console.error(e);
      alert(t("message.deleteError"));
    } finally {
      setLoading(false);
    }
  };

  // 폼에서 이미 등록된 국가(수정 중인 항목 제외)는 선택 목록에서 숨겨 중복 등록 방지
  const selectableCountries = useMemo(() => {
    const used = new Set(policies.filter((p) => p.idx !== form?.idx).map((p) => p.countryCode.toUpperCase()));
    return onlyNationalityOptions
      .filter((o) => o.value && !used.has(String(o.value).toUpperCase()))
      .sort((a, b) => String(a.label).localeCompare(String(b.label)));
  }, [onlyNationalityOptions, policies, form]);

  return (
    <div className={style.wrapper}>
      <div className={style.header}>
        <div>
          <h2 className={style.title}>{t("title")}</h2>
          <p className={style.description}>{t("description")}</p>
        </div>
        {!form && (
          <button className={style.primaryBtn} onClick={openCreate} disabled={loading}>
            + {t("actions.add")}
          </button>
        )}
      </div>

      {form && (
        <div className={style.formCard}>
          <h3 className={style.formTitle}>{form.idx == null ? t("form.createTitle") : t("form.editTitle")}</h3>
          <div className={style.formRow}>
            <label className={style.formLabel}>{t("form.countryLabel")}</label>
            <select
              className={style.select}
              value={form.countryCode}
              onChange={(e) => setForm({ ...form, countryCode: e.target.value })}
            >
              <option value="">{t("form.countryPlaceholder")}</option>
              {selectableCountries.map((c) => (
                <option key={c.value} value={c.value}>
                  {c.label} · {c.value}
                </option>
              ))}
            </select>
          </div>

          <div className={style.formRow}>
            <label className={style.formLabel}>{t("form.defaultReportTypeLabel")}</label>
            <select
              className={style.select}
              value={form.defaultReportType}
              onChange={(e) => setForm({ ...form, defaultReportType: e.target.value as ReportTypeValue })}
            >
              <option value={ReportTypeValue.STANDARD}>{t("reportType.standard")}</option>
              <option value={ReportTypeValue.BRAIN_ONLY}>{t("reportType.brainOnly")}</option>
            </select>
          </div>

          <div className={style.formRow}>
            <label className={style.formLabel}>{t("form.allowBothLabel")}</label>
            <label className={style.checkboxLabel}>
              <input
                type="checkbox"
                checked={form.allowBoth}
                onChange={(e) => setForm({ ...form, allowBoth: e.target.checked })}
              />
              <span>{form.allowBoth ? t("allowBoth.true") : t("allowBoth.false")}</span>
            </label>
          </div>

          <div className={style.formActions}>
            <button className={style.primaryBtn} onClick={save} disabled={loading}>
              {t("form.save")}
            </button>
            <button className={style.secondaryBtn} onClick={closeForm} disabled={loading}>
              {t("form.cancel")}
            </button>
          </div>
        </div>
      )}

      <table className={style.table}>
        <thead>
          <tr>
            <th>{t("columns.countryCode")}</th>
            <th>{t("columns.countryName")}</th>
            <th>{t("columns.defaultReportType")}</th>
            <th>{t("columns.allowBoth")}</th>
            <th>{t("columns.updateAt")}</th>
            <th>{t("columns.actions")}</th>
          </tr>
        </thead>
        <tbody>
          {policies.length === 0 && (
            <tr>
              <td colSpan={6} className={style.empty}>
                {t("message.empty")}
              </td>
            </tr>
          )}
          {policies.map((p) => (
            <tr key={p.idx}>
              <td className={style.codeCell}>{p.countryCode}</td>
              <td>{countryName(p.countryCode)}</td>
              <td>
                <span
                  className={`${style.badge} ${
                    p.defaultReportType === ReportTypeValue.BRAIN_ONLY ? style.badgeBrain : style.badgeStandard
                  }`}
                >
                  {reportTypeLabel(p.defaultReportType)}
                </span>
              </td>
              <td>{p.allowBoth ? t("allowBoth.true") : t("allowBoth.false")}</td>
              <td className={style.dateCell}>{p.updateAt?.replace("T", " ").slice(0, 16) ?? "-"}</td>
              <td>
                <button className={style.linkBtn} onClick={() => openEdit(p)}>
                  {t("actions.edit")}
                </button>
                <button className={`${style.linkBtn} ${style.danger}`} onClick={() => remove(p)}>
                  {t("actions.delete")}
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
